// Third party
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useMutation} from '@tanstack/react-query';

// Hooks
import { useLogoutAPI } from './useLogout';
import { useAppDispatch } from '../../useAppStore';
import { WelcomeController } from '../../../services';
import { useGetProfileInfo, useGetShooterProfileInfo } from '../profile';

// Components
import { isTokenValid} from '../../../utils';
import { RouteMapping } from '../../../constants';
import { profileDataActions } from '../../../store';
import { TLoginReq } from '../../../types/requests';
import { TLoginRes } from '../../../types/responses';

export function useLoginAPI() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { logout, clearAppState } = useLogoutAPI();
  const { enqueueSnackbar } = useSnackbar();

  const { refetch: getProfileInfo } = useGetProfileInfo();
  const { refetch: getShooterInfo } = useGetShooterProfileInfo();

  const loginAPI = async (data: TLoginReq) => {
    const payload = { identifier: data?.identifier.toLocaleLowerCase(), password: data.password };
    const rsp = await WelcomeController().login<TLoginRes>(payload);

    const { access_token } = rsp.data;
    if (access_token) isTokenValid(access_token);

    return rsp;
  };

  return useMutation({
    mutationFn: loginAPI,
    onSuccess: async (rsp: TLoginRes) => {
      if (rsp && rsp.statusText === 'OK') {
        await clearAppState();
        dispatch(profileDataActions.setAuthInfo(rsp.data));
        localStorage.setItem('authToken', rsp.data.access_token);

        enqueueSnackbar('Logged in successfully', {
          variant: 'success',
        });

        const { user_type } = rsp.data;

        if (user_type === 'PERSONAL') {
          await getShooterInfo();
        }

        if (user_type === 'MANAGED') {
          await getProfileInfo();
        }

        const role = rsp.data.role ? rsp.data.role.toUpperCase(): 'null';
        const userType = rsp.data.user_type?.toUpperCase();
        const accountType = rsp.data.account_type ? rsp.data.account_type.toUpperCase(): 'null';

        const routeKey = `${role}-${userType}-${accountType}`;

        const redirectPath = RouteMapping[routeKey] || '/unauthorized';

        navigate(redirectPath);

        return rsp;
      }
    },
    onError: async () => {
      enqueueSnackbar('Invalid username or password.', { variant: 'error' });
      await logout();
      return null;
    },
  });
}
